// Grids & Gantts — editing any field from its cell.
//
// A structure where only the summary, the dates and the numbers can be
// changed is a structure people leave to go and change things somewhere
// else. Every other column should edit too — and "every other" means a
// select, a multi-select, a user picker and a plain string, each written
// back in the shape its field actually wants.
//
// THE SHAPE IS NOT GUESSABLE, so it is asked for. `editmeta` says, per work
// item, which fields may be written and what they accept: a list of allowed
// values for a select, a user schema for a person, nothing for free text.
// Writing `"High"` into a field that wants `{ value: "High" }` fails with a
// 400 that reads like a server fault, so the meta is fetched rather than
// assumed.
//
// AND WHAT IT WRITES IS NOT WHAT IT SHOWS. The write shape is thin on
// purpose — { id } for a select, { accountId } for a user — so patching the
// page's own copy with it left a value with no label in it and the cell went
// to a dash while the change sat safely on the server. Both shapes now come
// out of ./editShape, and `onSave` is handed both.
//
// IT IS CACHED BY (project, work type), because that pair is exactly what
// decides a field's configuration and its allowed values in Jira. Caching
// per work item would mean a request per row, which on a structure of
// twenty-eight thousand is not a cache at all.
//
// Nothing is fetched until a cell is actually clicked.
//
// A SELECT AND A MULTI-SELECT ARE PICKED THE WAY THE WORK ITEM SCREEN PICKS
// THEM (2026-10-05) — the same two editors, not a look-alike. The old list
// wrote and closed on every click, so ticking three engineers onto a
// multi-select was three trips into the cell. Now: a search box, a checkbox
// per option with the ticked ones first, ticks that stay open and are saved
// after a short pause, and a click away that saves whatever is still
// pending. A single select is the screen's searchable list with the current
// value marked and None at the top; a pick saves and closes.

import { useEffect, useRef, useState } from 'react';
import Sheet from '../shared/Sheet';
import { patchFor } from './editShape';
import SelectEditor from '@modules/database/work-items/frontend/editors/SelectEditor';
import MultiSelectEditor from '@modules/database/work-items/frontend/editors/MultiSelectEditor';
import '@modules/database/work-items/frontend/editors/FieldEditor.css';

const metaCache = new Map();     // "PROJ:typeId" → { fields } | null
const pending = new Map();

const ctxKeyOf = (it) => {
  const p = (it && it.fields && it.fields.project && it.fields.project.key) || String((it && it.key) || '').split('-')[0];
  const t = (it && it.fields && it.fields.issuetype && (it.fields.issuetype.id || it.fields.issuetype.name)) || '?';
  return `${p}:${t}`;
};

export async function loadEditMeta(api, item) {
  const key = ctxKeyOf(item);
  if (metaCache.has(key)) return metaCache.get(key);
  if (pending.has(key)) return pending.get(key);
  const p = (async () => {
    let meta = null;
    try {
      const r = await api.getEditMeta(item.key);
      meta = (r && (r.fields || (r.data && r.data.fields))) || null;
    } catch { meta = null; }
    metaCache.set(key, meta);
    pending.delete(key);
    return meta;
  })();
  pending.set(key, p);
  return p;
}

// Warm the cache for the work types on screen, so the first click on a cell
// is not the first request.
export function prefetchEditMeta(api, items) {
  const seen = new Set();
  (items || []).forEach((it) => {
    const k = ctxKeyOf(it);
    if (seen.has(k) || metaCache.has(k) || pending.has(k)) return;
    seen.add(k);
    loadEditMeta(api, it).catch(() => {});
  });
}

const labelOf = (v) => (v == null ? '' : (typeof v === 'object'
  ? (v.displayName || v.value || v.name || v.key || String(v.id || '')) : String(v)));

// An allowed value as the work item screen's editors read one: an `id` they
// tell options apart by and a `name` they show. A person carries neither
// (accountId, displayName), so both are filled in; `orig` keeps the value as
// editmeta gave it, which is what ./editShape writes and the page shows.
export const asOption = (v) => (v && typeof v === 'object'
  ? { id: String(v.id != null ? v.id : (v.accountId || v.value || v.name || '')), name: labelOf(v), iconUrl: v.iconUrl, orig: v }
  : { id: String(v), name: String(v), orig: v });
const idsOf = (list) => (list || []).map((o) => o.id).sort().join('|');
const SAVE_PAUSE = 800;   // the work item screen's own pause between a tick and its save

export default function EditField({ item, fieldId, text, ui, S, api, onSave, align, narrow = false }) {
  const anchor = useRef(null);
  const [open, setOpen] = useState(false);
  const [def, setDef] = useState(undefined);   // undefined = not loaded, null = not editable
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // A MULTI-SELECT'S TICKS while the picker is open: what is shown ticked,
  // and what was last written, so a click away saves only what is pending.
  const [ticks, setTicks] = useState([]);
  const ticksRef = useRef([]);
  const savedIds = useRef('');
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const nowList = () => {
    const v = item && item.fields ? item.fields[fieldId] : null;
    return v == null ? [] : (Array.isArray(v) ? v : [v]);
  };

  const start = async () => {
    // A second click on the cell while its picker is open keeps the ticks.
    if (open) return;
    setErr(null);
    const cur = nowList().map(asOption);
    ticksRef.current = cur; setTicks(cur); savedIds.current = idsOf(cur);
    setOpen(true);
    if (def !== undefined) return;
    const meta = await loadEditMeta(api, item);
    setDef((meta && meta[fieldId]) || null);
  };

  // BOTH SHAPES GO BACK. The first is for the server, the second is what the
  // page should now believe the work item holds — see ./editShape for why
  // they are not the same object. A multi-select's tick keeps the picker
  // open; everything else closes it.
  const write = async (picked, { keepOpen = false } = {}) => {
    setBusy(true); setErr(null);
    try {
      const { write: payload, display } = patchFor(fieldId, def, picked);
      await onSave(payload, display);
      if (!keepOpen) setOpen(false);
      return true;
    } catch (e) {
      setErr((e && e.response && e.response.data && (e.response.data.details || e.response.data.error)) || (e && e.message) || 'refused');
      return false;
    } finally { setBusy(false); }
  };

  // The ticks as they stand, written if they differ from what was last
  // written. A tick waits SAVE_PAUSE for the next one; a click away (or
  // Escape, or Done) writes at once and closes.
  const flushTicks = async ({ keepOpen }) => {
    clearTimeout(timer.current);
    const list = ticksRef.current;
    if (idsOf(list) === savedIds.current) { if (!keepOpen) setOpen(false); return; }
    const ids = idsOf(list);
    if (await write(list.map((o) => o.orig), { keepOpen })) savedIds.current = ids;
  };
  const onTicks = (next) => {
    const list = (next || []).filter(Boolean);
    ticksRef.current = list; setTicks(list);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { flushTicks({ keepOpen: true }); }, SAVE_PAUSE);
  };

  // A LIST OF NOTHING IS NOT A LIST. A free-text field's editmeta carries
  // `allowedValues: []`, and an empty array is truthy — so "PO Number:"
  // opened as a picker with one entry, "— none —", and no way to type. A
  // picker needs values to pick from; without them the field is typed into.
  const allowed = def && Array.isArray(def.allowedValues) && def.allowedValues.length ? def.allowedValues : null;
  const isArray = def && def.schema && def.schema.type === 'array';
  const options = allowed ? allowed.map(asOption) : [];
  const current = nowList().map(labelOf);
  // Closing the sheet — a click away, Escape, Done — saves a multi-select's
  // pending ticks; anything else simply closes.
  const close = () => { if (def && allowed && isArray) flushTicks({ keepOpen: false }); else setOpen(false); };

  // THE WHOLE CELL IS THE TARGET. An empty field was an em-dash floating in a
  // 150px cell, and the only way to set a value was to hit the dash — which
  // is not an interaction, it is a test of aim. The cell fills its column and
  // its row, so anywhere in it opens the picker.
  const cell = (
    <span role="button" tabIndex={-1}
      title={text ? `${text} — click anywhere in this cell to change it` : 'Empty — click anywhere in this cell to set a value'}
      onClick={(e) => { e.stopPropagation(); start(); }}
      style={{
        cursor: 'pointer', width: '100%', height: '100%', display: 'flex', alignItems: 'center',
        justifyContent: align === 'right' ? 'flex-end' : (align === 'center' ? 'center' : 'flex-start'),
        minWidth: 0, opacity: busy ? 0.5 : 1,
        // The cell gave its padding up so that these twelve pixels are part of
        // the click target; the target has to put them back or the text sits
        // flush against the column's border.
        padding: '0 12px', boxSizing: 'border-box',
      }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {busy ? '…' : (text || <span style={{ color: ui.faint }}>—</span>)}
      </span>
    </span>
  );

  return (
    <span ref={anchor} style={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%', minWidth: 0 }}>
      {cell}
      {err ? <span title={err} style={{ color: ui.red, fontSize: 11, cursor: 'help', flex: '0 0 auto' }}>⚠</span> : null}
      {open && (
        <Sheet anchorRef={anchor} onClose={close} ui={ui} width={allowed ? 300 : 262} maxHeight={allowed ? 380 : 330} narrow={narrow} title={(def && def.name) || 'Change this value'}>
          {def === undefined ? <div style={{ ...S.tiny, padding: '6px 8px' }}>reading the field…</div> : null}
          {def === null ? (
            <div style={{ ...S.tiny, padding: '6px 8px' }}>
              This field cannot be edited on {item.key} — the work item’s own screen does not offer it.
            </div>
          ) : null}

          {/* A MULTI-SELECT: the work item screen's own editor — search,
              a checkbox per option, the ticked ones first. Ticks stay open
              and save after a pause; a click away saves the rest. */}
          {def && allowed && isArray ? (
            <>
              <div style={{ ...S.tiny, fontWeight: 700, padding: '2px 4px 6px', display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{def.name}</span>
                <span style={{ marginLeft: 'auto', fontWeight: 400, whiteSpace: 'nowrap' }}>{busy ? 'saving…' : `${ticks.length} ticked`}</span>
              </div>
              <MultiSelectEditor value={ticks} onChange={onTicks} options={options} loading={false} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 2px 0' }}>
                <span style={S.tiny}>Saved as you tick — {narrow ? 'Done when finished.' : 'click away when done.'}</span>
                {/* A phone's sheet has its own Done at the top. */}
                {!narrow ? <button onClick={close} style={{ ...S.pill(true), marginLeft: 'auto', padding: '3px 12px', fontSize: 12 }}>Done</button> : null}
              </div>
            </>
          ) : null}

          {/* A SINGLE SELECT: the work item screen's own list — search, None
              at the top, the current value marked; a pick saves and closes. */}
          {def && allowed && !isArray ? (
            <>
              <div style={{ ...S.tiny, fontWeight: 700, padding: '2px 4px 2px' }}>{def.name}</div>
              {/* WHAT IT IS NOW, before what it could be — the current value
                  may be forty options down the list. */}
              <div style={{ ...S.tiny, padding: '0 4px 6px', display: 'flex', gap: 5, alignItems: 'baseline' }}>
                <span style={{ flex: '0 0 auto' }}>Now:</span>
                <strong style={{ color: current.length ? ui.fg : ui.faint, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {current.length ? current.join(', ') : 'nothing'}
                </strong>
              </div>
              <div className="field-editor-multiselect-wrapper">
                <SelectEditor value={ticks[0] || null} options={options} loading={false} disabled={busy}
                  onChange={() => {}} onAutoSave={(opt) => write(opt ? [opt.orig] : [])} />
              </div>
            </>
          ) : null}

          {def && !allowed ? (
            <>
              <div style={{ ...S.tiny, fontWeight: 700, padding: '2px 6px 6px' }}>{def.name}</div>
              <input autoFocus={!narrow} defaultValue={text || ''}
                onFocus={(e) => e.currentTarget.select()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') write([e.currentTarget.value]);
                  if (e.key === 'Escape') setOpen(false);
                }}
                style={{ ...S.input, padding: '5px 8px', fontSize: 12.5 }} />
              <div style={{ ...S.tiny, marginTop: 5 }}>Enter to save, Escape to leave it.</div>
            </>
          ) : null}
        </Sheet>
      )}
    </span>
  );
}
